import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const Shell = ({ children, testid }) => (
  <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid={testid}>
    <div className="cx-card rounded-3xl p-10 max-w-md w-full text-center">{children}</div>
  </div>
);

export function PaymentSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");
  const [info, setInfo] = useState(null);
  const tries = useRef(0);
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("failed"); return undefined; }
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (!alive) return;
        if (data.payment_status === "paid") { setInfo(data); return setState("paid"); }
        if (["expired", "failed"].includes(data.payment_status)) return setState("failed");
      } catch { if (!alive) return; }
      tries.current += 1;
      if (tries.current >= 10) return setState("pending");
      setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; };
  }, [params]);
  return (
    <Shell testid="payment-success-page">
      {state === "checking" && (<><Loader2 className="w-12 h-12 mx-auto text-[#3ad7ff] animate-spin" /><p className="mt-5 text-white" data-testid="payment-checking">{t("pay.checking")}</p></>)}
      {state === "paid" && (<><CheckCircle2 className="w-14 h-14 mx-auto text-[#22e3a1]" /><h1 className="mt-5 text-2xl font-bold text-white" data-testid="payment-paid">{t("pay.success")}</h1><p className="mt-2 text-sm text-slate-400">{t("pay.success_sub")}</p>{info?.clips ? <p className="mt-3 text-lg font-bold text-white font-mono" data-testid="payment-clips">+{info.clips}{info.promo_bonus ? <span className="text-[#22e3a1]" data-testid="payment-promo-bonus"> +{info.promo_bonus} {t("cr.bonus").toLowerCase()}</span> : null}</p> : null}{info?.promo_missed ? <p className="mt-2 text-xs text-amber-300" data-testid="payment-promo-missed">{t("promo.missed")}</p> : null}</>)}
      {state === "pending" && (<><Clock className="w-12 h-12 mx-auto text-amber-300" /><p className="mt-5 text-white" data-testid="payment-pending">{t("pay.pending")}</p></>)}
      {state === "failed" && (<><XCircle className="w-12 h-12 mx-auto text-[#ff4d6d]" /><p className="mt-5 text-white" data-testid="payment-failed">{t("pay.failed")}</p></>)}
      {state !== "checking" && (
        <div className="mt-8 flex flex-col gap-3">
          <Link to="/credits" className="cx-btn rounded-full py-3 text-sm font-semibold" data-testid="payment-back-credits">{t("pay.back")}</Link>
          <Link to="/dashboard" className="cx-metal rounded-full py-3 text-sm" data-testid="payment-to-clips">{t("pay.to_clips")}</Link>
        </div>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  const { t } = useI18n();
  return (
    <Shell testid="payment-cancel-page">
      <XCircle className="w-12 h-12 mx-auto text-slate-400" />
      <h1 className="mt-5 text-2xl font-bold text-white">{t("pay.cancel")}</h1>
      <p className="mt-2 text-sm text-slate-400">{t("pay.cancel_sub")}</p>
      <Link to="/credits" className="cx-btn mt-8 inline-block rounded-full px-8 py-3 text-sm font-semibold" data-testid="payment-cancel-back">{t("pay.back")}</Link>
    </Shell>
  );
}
